import React, { useRef, useEffect, useState } from 'react';
import './text-editor-no-lib.styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBold, faBorderStyle, faCode, faItalic, faList, faList12, faP, faQuoteLeft, faUnderline } from '@fortawesome/free-solid-svg-icons';
import { faImage } from '@fortawesome/free-regular-svg-icons';

const TextEditorNoLib = ({ 
  onChange, 
  initialValue,
  handleImageUpload 
}) => {
  const editorRef = useRef(null);

  // State to track which buttons should have the "active" class
  const [activeStyles, setActiveStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
    h1: false,
    h2: false,
    p: false,
    blockquote: false,
    pre: false,
    insertOrderedList: false,
    insertUnorderedList: false,
  });

  // Update the active state of buttons based on the current cursor position
  const updateToolbar = () => {
    const isBlock = (tag) => {
      // queryCommandValue returns the tag name for formatBlock
      const val = document.queryCommandValue('formatBlock');
      return val === tag || val === tag.toLowerCase();
    };

    setActiveStyles({
      bold: document.queryCommandState('bold') && !isBlock('h1') && !isBlock('h2'), // Don't show bold as active if we're in a header
      italic: document.queryCommandState('italic') && !isBlock('blockquote'),
      underline: document.queryCommandState('underline'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      h1: isBlock('h1'),
      h2: isBlock('h2'),
      p: isBlock('p'),
      blockquote: isBlock('blockquote'),
      pre: isBlock('pre'),
    });
  };

  // Initialize content once on mount
  useEffect(() => {
    if (editorRef.current && initialValue) {
      editorRef.current.innerHTML = initialValue;
    }
  }, [initialValue]);

  const emitChange = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    updateToolbar(); // Update buttons when content changes
  };

  const runCommand = (command, value = null) => {
    // 1. Check if we are dealing with a Block Format (H1, H2, Blockquote, etc.)
    if (command === 'formatBlock') {
      const isAlreadyActive = activeStyles[value.toLowerCase()];

      // 2. If already active, "Toggle Off" by converting to a normal Paragraph
      if (isAlreadyActive) {
        document.execCommand('formatBlock', false, 'P');
      } else {
        document.execCommand('formatBlock', false, value);
      }
    } else {
      // 3. Inline styles (Bold, Italic, etc.) toggle automatically by the browser
      document.execCommand(command, false, value);
    }
    editorRef.current.focus();
    emitChange();
  };

  const handlePaste = (e) => {
    // Intercept paste to strip all HTML/CSS/Inline styles
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');

    // Inserts the text at the cursor as raw text
    document.execCommand('insertText', false, text);
    emitChange();
  };

  const insertImage = async (file) => {
    // Replace prompt with your logic to get the hosted URL from cloud storage
    const url = await handleImageUpload(file);
    console.log('Image uploaded, URL:', url);
    if (url) {
      const imgHtml = `
      <div style="width: 100%; display: flex; justify-content: center;">
      <img style="width: 100%;" src="${url}" alt="user content" />
      </div>`;
      document.execCommand('insertHTML', false, imgHtml);
      emitChange();
    }
  };

  return (
    <div className="rte-container">
      <div className="rte-toolbar">
        {/* Block Styles */}
        <button
          type="button"
          className={activeStyles.h1 ? 'active' : ''}
          onClick={() => runCommand('formatBlock', 'H1')}>
          H1
        </button>
        <button
          type="button"
          className={activeStyles.h2 ? 'active' : ''}
          onClick={() => runCommand('formatBlock', 'H2')}>
          H2
        </button>
        <div></div>
        {/* <button 
          type="button" 
          className={activeStyles.p ? 'active' : ''}
          onClick={() => runCommand('formatBlock', 'P')}>
          <FontAwesomeIcon icon={faP} />
        </button> */}

        {/* Inline Styles */}
        <button
          type="button"
          className={activeStyles.bold ? 'active' : ''}
          onClick={() => runCommand('bold')}>
          <FontAwesomeIcon icon={faBold} />
        </button>
        <button
          type="button"
          className={activeStyles.italic ? 'active' : ''}
          onClick={() => runCommand('italic')}>
          <FontAwesomeIcon icon={faItalic} />
        </button>
        <button
          type="button"
          className={activeStyles.underline ? 'active' : ''}
          onClick={() => runCommand('underline')}>
          <FontAwesomeIcon icon={faUnderline} />
        </button>
        <div></div>
        {/* Lists */}
        <button
          type="button"
          className={activeStyles.orderedList ? 'active' : ''}
          onClick={() => runCommand('insertOrderedList')}>
          <FontAwesomeIcon icon={faList12} />
        </button>
        <button
          type="button"
          className={activeStyles.unorderedList ? 'active' : ''}
          onClick={() => runCommand('insertUnorderedList')}>
          <FontAwesomeIcon icon={faList} />
        </button>
        <div></div>
        {/* Special Blocks */}
        <button
          type="button"
          className={activeStyles.blockquote ? 'active' : ''}
          onClick={() => runCommand('formatBlock', 'blockquote')}>
          <FontAwesomeIcon icon={faQuoteLeft} />
        </button>
        <button
          type="button"
          className={activeStyles.pre ? 'active' : ''}
          onClick={() => runCommand('formatBlock', 'PRE')}>
          <FontAwesomeIcon icon={faCode} />
        </button>
        <div></div>
        {/* Images */}
        {
          (handleImageUpload) &&
          <>
            <input
              type="file"
              id="text-editor_imageUpload"
              style={{ display: 'none' }}
              accept="image/*"
              onChange={(e) => insertImage(e.target.files[0])}
            />
            <button
              type="button"
              onClick={() => document.getElementById('text-editor_imageUpload').click()}>
              <FontAwesomeIcon icon={faImage} />
            </button>
          </>
        }
      </div>

      <div
        ref={editorRef}
        className="rte-content"
        contentEditable={true}
        onInput={emitChange}
        onPaste={handlePaste}
        spellCheck="true"
      />
    </div>
  );
};

export default TextEditorNoLib;